/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import Text from '../ts-components/Text'

export interface MetricCardData {
  title: string
  value: number
  icon: React.ReactNode
}

interface MetricCardProps extends MetricCardData {
  onClick?: () => void
}

/**
 * Reusable MetricCard component for displaying post insights metrics
 */
const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  onClick,
}) => {
  return (
    <div
      css={css`
        padding: 8px 8px 8px 16px;
        position: relative;
        background: ${colors.SECONDARY_200};
        border-radius: 8px;
        display: flex;
        justify-content: center;
        align-items: flex-end;
        gap: 6px;
        cursor: ${onClick ? 'pointer' : 'default'};
        transition: background-color 0.2s ease;

        &:hover {
          background-color: ${onClick
            ? colors.SECONDARY_100
            : colors.SECONDARY_200};
        }
      `}
      onClick={onClick}
    >
      {/* Content Section */}
      <div
        css={css`
          flex: 1 1 0;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
          align-items: flex-start;
          gap: 4px;
        `}
      >
        {/* Title */}
        <div
          css={css`
            align-self: stretch;
          `}
        >
          <Text
            variant='paragraphMedium'
            weight='bold'
            color={colors.PRIMARY}
            style='font-size: 16px; line-height: 23.2px;'
          >
            {title}
          </Text>
        </div>

        {/* Value */}
        <div
          css={css`
            align-self: stretch;
          `}
        >
          <Text
            variant='heading4'
            weight='semiBold'
            color={colors.WHITE}
            style="font-size: 20px; font-family: 'Barlow Condensed', sans-serif; font-weight: 600; line-height: 24px;"
          >
            {value.toLocaleString()}
          </Text>
        </div>
      </div>

      {/* Icon */}
      <div
        css={css`
          width: 24px;
          height: 24px;
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
        `}
      >
        {icon}
      </div>
    </div>
  )
}

export default MetricCard
